/* OutletIntelligencePage — Operational Intelligence (Bundle 2.9 · 5 May 2026).
 *
 * Architecture (Dipali doctrine 5 May 2026):
 *   - Stays is an outlet like any other — same row, same pill design,
 *     same click behaviour. No separate section.
 *   - Single-row outlet picker with horizontal scroll on narrow screens.
 *   - Tabs adapt to the outlet's integration:
 *       Restaurant (POS) → Revenue · Menu · Kitchen · Cost · Service · Delivery
 *       Stays      (PMS) → Revenue · Occupancy · Channels · Guest · Cost
 *   - "Sync now" button forces a fresh eZee pull when the floor manager
 *     wants to verify a just-rung order without waiting for the cron.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Activity, IndianRupee, BedDouble, ChefHat, DollarSign,
         Users as UsersIcon, Sparkles, Truck, Menu as MenuIcon,
         Network } from 'lucide-react';
import RevenueIntelligenceTab from '../components/outletIntel/RevenueIntelligenceTab';
import StaysRevenueIntelligenceTab from '../components/outletIntel/StaysRevenueIntelligenceTab';
import OccupancyIntelligenceTab from '../components/outletIntel/OccupancyIntelligenceTab';
import ChannelsIntelligenceTab from '../components/outletIntel/ChannelsIntelligenceTab';
import MenuIntelligenceTab from '../components/outletIntel/MenuIntelligenceTab';
import KitchenIntelligenceTab from '../components/outletIntel/KitchenIntelligenceTab';
import CostIntelligenceTab from '../components/outletIntel/CostIntelligenceTab';
import GuestIntelligenceTab from '../components/outletIntel/GuestIntelligenceTab';
import ServiceIntelligenceTab from '../components/outletIntel/ServiceIntelligenceTab';
import DeliveryIntelligenceTab from '../components/outletIntel/DeliveryIntelligenceTab';
import SyncNowButton from '../components/SyncNowButton';

// Stays-side cost data isn't logged yet (Saket only enters
// restaurant purchases). Render a graceful placeholder instead of
// letting CostIntelligenceTab 404 against `/cost-intel/stays_ketti/...`.
function StaysCostPlaceholder() {
  return (
    <div className="rounded-xl bg-white p-6" style={{ border: '1px solid #E5E1DC' }}
         data-testid="stays-cost-placeholder">
      <h3 className="text-sm font-semibold mb-2" style={{ color: '#1B2A4A' }}>
        Stays · Room operations cost
      </h3>
      <p className="text-[13px] mb-3" style={{ color: '#6B7280' }}>
        No purchase data logged for Stays yet. Saket logs the restaurant
        purchases (Celeste, ANMA, XIN) but not the hotel side.
      </p>
      <p className="text-[12px]" style={{ color: '#9CA3AF' }}>
        This tab will populate automatically once room-ops purchases
        (housekeeping, F&amp;B inventory for in-room dining, linen,
        consumables) are entered against `outlet_id=stays_ketti` in the
        purchase ledger.
      </p>
    </div>
  );
}

const TABS = [
  { id: 'revenue',   label: 'Revenue',   icon: IndianRupee, live: true },
  { id: 'occupancy', label: 'Occupancy', icon: BedDouble,   live: true, stays_only: true },
  { id: 'channels',  label: 'Channels',  icon: Network,     live: true, stays_only: true },
  { id: 'menu',      label: 'Menu',      icon: MenuIcon,    live: true, fnb_only: true },
  { id: 'kitchen',   label: 'Kitchen',   icon: ChefHat,     live: true, fnb_only: true },
  { id: 'cost',      label: 'Cost',      icon: DollarSign,  live: true },
  { id: 'guest',     label: 'Guest',     icon: UsersIcon,   live: true, stays_only: true },
  { id: 'service',   label: 'Service',   icon: Sparkles,    live: true, fnb_only: true },
  { id: 'delivery',  label: 'Delivery',  icon: Truck,       live: true, fnb_only: true },
];

// Compact display name for the pill — keeps all 6 outlets on one row
// without horizontal scroll on standard 1440+ widths. Falls back to
// the API's `display_name` for any outlet not in this map.
const PILL_LABEL = {
  celeste_ketti:   'Celeste · Ketti',
  anma_coonoor:    'ANMA · Coonoor',
  anma_ooty:       'ANMA · Ooty',
  xin_hsr:         'XIN · HSR',
  xin_koramangala: 'XIN · Koramangala',
  stays_ketti:     'Stays · Ketti',
};

// Stays must always render last in the pill row (Dipali doctrine
// 5 May 2026: same row, Stays at the end).
const PILL_ORDER = [
  'celeste_ketti', 'anma_coonoor', 'anma_ooty',
  'xin_hsr', 'xin_koramangala', 'stays_ketti',
];

export default function OutletIntelligencePage() {
  const [outlets, setOutlets] = useState([]);
  const [, setMode] = useState('mock');
  const [loading, setLoading] = useState(true);
  const [refreshNonce, setRefreshNonce] = useState(0); // bumps to force tab re-fetch after sync
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const outletId = searchParams.get('outlet') || 'celeste_ketti';
  const activeTab = searchParams.get('tab') || 'revenue';

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/outlet-intel/outlets');
        // Sort to canonical order (Stays last) so the picker is stable
        const items = (res.data.items || []).slice().sort((a, b) => {
          const ia = PILL_ORDER.indexOf(a.outlet_id);
          const ib = PILL_ORDER.indexOf(b.outlet_id);
          return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
        });
        setOutlets(items);
        setMode(res.data.mode || 'mock');
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const outlet = useMemo(() => outlets.find(o => o.outlet_id === outletId), [outlets, outletId]);
  const isStays = outletId === 'stays_ketti';

  const setOutlet = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('outlet', id);
    // Default to revenue when switching outlets if the active tab
    // doesn't apply to the new outlet's scope.
    const tabDef = TABS.find(t => t.id === activeTab);
    const nextIsStays = id === 'stays_ketti';
    if (tabDef?.stays_only && !nextIsStays) next.set('tab', 'revenue');
    if (tabDef?.fnb_only && nextIsStays) next.set('tab', 'revenue');
    setSearchParams(next);
  };
  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    setSearchParams(next);
  };

  if (loading) return <div className="p-8 text-sm" style={{ color: '#6B7280' }}>Loading outlets…</div>;

  const visibleTabs = TABS.filter(t => {
    if (t.stays_only && !isStays) return false;
    if (t.fnb_only && isStays) return false;
    return true;
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F5F2' }} data-testid="outlet-intel-page">
      {/* Header */}
      <div style={{ backgroundColor: '#1B2A4A', color: 'white' }} className="px-6 py-5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[11px] uppercase tracking-widest" style={{ color: '#9DB5CC' }}>
                ScaleBoard · Outlet Intelligence
              </p>
              <h1 className="text-2xl font-semibold mt-1">Operational Intelligence</h1>
            </div>
            <button
              onClick={() => navigate('/ceo')}
              className="text-[12px] px-3 py-1.5 rounded-full border"
              style={{ borderColor: '#9DB5CC', color: '#CBDCEC' }}
              data-testid="back-to-ceo-btn"
            >
              ← CEO Dashboard
            </button>
          </div>

          {/* Outlet picker — single row, scrolls horizontally on narrow viewports.
              Bundle 2.9 (5 May 2026): Stays sits at the end of this row, same
              pill style as the restaurants. No second-row wrap, no divider. */}
          <div
            className="flex gap-2 mt-4 overflow-x-auto pb-1"
            style={{ scrollbarWidth: 'thin' }}
            data-testid="outlet-picker"
          >
            {outlets.map(o => {
              const active = o.outlet_id === outletId;
              const label = PILL_LABEL[o.outlet_id] || o.display_name;
              const badge = o.integration === 'absolute' ? 'PMS' : 'POS';
              return (
                <button
                  key={o.outlet_id}
                  onClick={() => setOutlet(o.outlet_id)}
                  data-testid={`outlet-btn-${o.outlet_id}`}
                  className="text-[12px] px-3 py-1.5 rounded-full border transition flex-shrink-0 whitespace-nowrap"
                  style={{
                    backgroundColor: active ? '#F59E0B' : 'transparent',
                    borderColor: active ? '#F59E0B' : '#9DB5CC',
                    color: active ? '#1B2A4A' : '#CBDCEC',
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {label}
                  <span className="ml-2 text-[10px] opacity-60">{badge}</span>
                </button>
              );
            })}
          </div>

          {/* Live-source row + Sync Now button */}
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <div className="text-[11px] flex items-center gap-2" style={{ color: '#9DB5CC' }}>
              <Activity size={12} />
              <span>
                Data source:{' '}
                <strong style={{ color: '#34D399' }}>
                  LIVE ({isStays ? 'eZee Absolute PMS' : 'eZee api.ipos247.com'})
                </strong>
                {' · '}
                {isStays ? 'folio pipeline' : 'sales-items pipeline'}
              </span>
            </div>
            <SyncNowButton
              divisionId="me"
              variant="dark"
              testid="outlet-intel-sync-btn"
              onComplete={() => setRefreshNonce(n => n + 1)}
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 mt-4">
        <div className="flex gap-1 border-b overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
          {visibleTabs.map(t => {
            const Icon = t.icon;
            const active = t.id === activeTab;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className="flex items-center gap-1.5 px-4 py-2.5 text-[12px] border-b-2 transition"
                style={{
                  borderColor: active ? '#F59E0B' : 'transparent',
                  color: active ? '#1B2A4A' : '#6B7280',
                  fontWeight: active ? 600 : 400,
                  whiteSpace: 'nowrap',
                }}
              >
                <Icon size={14} />
                {t.label}
                {!t.live && <span className="text-[9px] px-1.5 py-0.5 rounded-full ml-1"
                  style={{ backgroundColor: '#F3F4F6', color: '#9CA3AF' }}>soon</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content — `key` includes refreshNonce so the SyncNowButton
          callback forces a fresh fetch in the active tab without a hard
          reload. */}
      <div className="max-w-7xl mx-auto px-6 py-6" key={`${outletId}-${activeTab}-${refreshNonce}`}>
        {activeTab === 'revenue' && outlet && isStays && (
          <StaysRevenueIntelligenceTab />
        )}
        {activeTab === 'revenue' && outlet && !isStays && (
          <RevenueIntelligenceTab outletId={outletId} outlet={outlet} />
        )}
        {activeTab === 'occupancy' && isStays && (
          <OccupancyIntelligenceTab />
        )}
        {activeTab === 'channels' && isStays && (
          <ChannelsIntelligenceTab />
        )}
        {activeTab === 'menu' && outlet && !isStays && (
          <MenuIntelligenceTab outletId={outletId} outlet={outlet} />
        )}
        {activeTab === 'kitchen' && outlet && !isStays && (
          <KitchenIntelligenceTab outletId={outletId} outlet={outlet} />
        )}
        {activeTab === 'cost' && outlet && !isStays && (
          <CostIntelligenceTab outletId={outletId} outlet={outlet} />
        )}
        {activeTab === 'cost' && isStays && (
          <StaysCostPlaceholder />
        )}
        {activeTab === 'guest' && isStays && (
          <GuestIntelligenceTab />
        )}
        {activeTab === 'service' && outlet && !isStays && (
          <ServiceIntelligenceTab outletId={outletId} outlet={outlet} />
        )}
        {activeTab === 'delivery' && outlet && !isStays && (
          <DeliveryIntelligenceTab outletId={outletId} outlet={outlet} />
        )}
      </div>
    </div>
  );
}
